export const truncate = (value: string, lengthLimit: number): string =>
  value.length > lengthLimit
    ? `${value.substring(0, lengthLimit - 3)}...`
    : value;

export const escapeRegularExpression = (pattern: string): string =>
  pattern.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');

export function isStringNotBlank(text: string): boolean {
  return text.trim().length > 0;
}

const isNestedAmount = (
  item: string | number | undefined | {amount: string}
): item is {amount: string} => {
  if (!item || typeof item === 'string' || typeof item === 'number') {
    return false;
  }
  return 'amount' in item && Boolean(item.amount);
};

export const toDefaultNumberFormat = (
  value: number,
  options: Intl.NumberFormatOptions = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }
): string => new Intl.NumberFormat('de-CH', options).format(value);

/** Formats a value to a currency amount. It ensures two decimal places and optionally shows a currency code. */
export const toCurrencyAmount = (
  amount: string | number | undefined | {amount: string},
  {fallback = '—', currency}: {fallback?: string; currency?: string} = {}
): string => {
  const localAmount = isNestedAmount(amount) ? amount.amount : amount;

  // check if value is a null value but not zero itself
  // the localAmount === undefined would not be necessary but the typescript compiler seems not to understand that
  if (
    localAmount === undefined ||
    (!localAmount && (typeof amount !== 'number' || Number.isNaN(amount)))
  ) {
    return fallback;
  }

  const numericAmount =
    typeof localAmount === 'string'
      ? parseFloat(prepareNumericString(localAmount))
      : localAmount;

  // The locale is currently hard-coded to avoid problems with other locales (for example,
  // comma instead of period as the decimal separator).
  return toDefaultNumberFormat(numericAmount, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...(currency ? {currency, style: 'currency'} : {}),
  });
};

export const toCurrencyAmountForInput = (
  amount: string | number | undefined | {amount: string},
  {fallback = '—'}: {fallback?: string} = {}
): string => {
  const localAmount = isNestedAmount(amount) ? amount.amount : amount;

  // check if value is a null value but not zero itself
  // the localAmount === undefined would not be necessary but the typescript compiler seems not to understand that
  if (
    localAmount === undefined ||
    (!localAmount && (typeof amount !== 'number' || Number.isNaN(amount)))
  ) {
    return fallback;
  }

  const numericAmount =
    typeof localAmount === 'string'
      ? parseFloat(prepareNumericString(localAmount))
      : localAmount;

  return numericAmount.toFixed(2);
};

const isNestedTaxRate = (
  item: string | number | undefined | {ratioAsDecimal: string}
): item is {ratioAsDecimal: string} => {
  if (!item || typeof item === 'string' || typeof item === 'number') {
    return false;
  }
  return 'ratioAsDecimal' in item && Boolean(item.ratioAsDecimal);
};

/** Formats a value to a tax percentage. It ensures two decimal places and optionally hides percentage sign. */
export const toTaxRate = (
  taxRate: string | number | undefined | {ratioAsDecimal: string},
  {
    fallback = '—',
    showUnit = true,
  }: {fallback?: string; showUnit?: boolean} = {}
): string => {
  const localTaxRate = isNestedTaxRate(taxRate)
    ? taxRate.ratioAsDecimal
    : taxRate;

  // check if value is a null value but not zero itself
  // the localTaxRate === undefined would not be necessary but the typescript compiler seems not to understand that
  if (
    localTaxRate === undefined ||
    (!localTaxRate && (typeof taxRate !== 'number' || Number.isNaN(taxRate)))
  ) {
    return fallback;
  }

  const numericTaxRate =
    (typeof localTaxRate === 'string'
      ? parseFloat(prepareNumericString(localTaxRate))
      : localTaxRate) * 100;

  // The locale is currently hard-coded to avoid problems with other locales (for example,
  // comma instead of period as the decimal separator).
  const formattedTaxRate = toDefaultNumberFormat(numericTaxRate, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  return showUnit ? `${formattedTaxRate} %` : formattedTaxRate;
};

/**
 * Removes leading/trailing whitespace and grouping separators.
 */
export const prepareNumericString = (numeric: string): string =>
  numeric
    .trim()
    .replaceAll(/,/gu, '.')
    .replaceAll(/[^\-0-9.]/gu, '');

export function splitFirstOccurrence(
  description: string,
  separator: string
): [string, string] {
  const [title, ...rest] = description.split(separator);
  return [(title ?? '').trim(), rest.join('\n').trim()];
}
